import { useEffect, useMemo, useState } from "react";
import {
  FileText, Loader2, Save, Search, Filter, Users, Layers, AlertCircle,
  CheckCircle2, Info, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORY_COLORS = {
  angebot:   "bg-emerald-500/10 border-emerald-500/40 text-emerald-200",
  vertrag:   "bg-sky-500/10 border-sky-500/40 text-sky-200",
  rechnung:  "bg-amber-500/10 border-amber-500/40 text-amber-200",
  protokoll: "bg-violet-500/10 border-violet-500/40 text-violet-200",
  uebergabe: "bg-rose-500/10 border-rose-500/40 text-rose-200",
  kunde:     "bg-cyan-500/10 border-cyan-500/40 text-cyan-200",
  sonstige:  "bg-zinc-500/10 border-zinc-500/40 text-zinc-300",
};

/**
 * Iter89m (2026-02-21) — Zentrale Admin-Ansicht der Dokumenten-Registry.
 *
 * Zeigt alle System-Vorlagen (bereits im Code implementierte PDF-Generatoren)
 * und lässt den Admin folgende Metadaten pflegen:
 *   - Kategorie (Angebot, Vertrag, …)
 *   - Pipeline-Stages (auf welchen Stages soll das Dokument angeboten werden?)
 *   - Empfänger (Kunde, Installateur, …)
 *   - Beschreibung + Anzeigename
 *   - Aktiv-Flag
 */
export default function AdminDocumentTemplates() {
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [m, r] = await Promise.all([
        api.get("/document-templates/meta"),
        api.get("/document-templates"),
      ]);
      setMeta(m.data);
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Vorlagen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let out = items;
    if (catFilter) out = out.filter((t) => t.category === catFilter);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      out = out.filter((t) =>
        (t.name || "").toLowerCase().includes(s) ||
        (t.description || "").toLowerCase().includes(s) ||
        (t.key || "").toLowerCase().includes(s)
      );
    }
    return out;
  }, [items, q, catFilter]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const k = t.category || "sonstige";
      if (!map[k]) map[k] = [];
      map[k].push(t);
    });
    return map;
  }, [filtered]);

  const patch = async (id, changes) => {
    setSavingId(id);
    try {
      const r = await api.patch(`/document-templates/${id}`, changes);
      const updated = r.data.template;
      setItems((prev) => prev.map((t) => (t.id === id ? updated : t)));
      toast.success(`Aktualisiert · v${updated.version}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Änderung fehlgeschlagen");
    } finally {
      setSavingId(null);
    }
  };

  const reseed = async (force) => {
    if (force && !window.confirm("System-Defaults zurücksetzen? Alle Änderungen an Kategorien, Stages und Empfängern gehen verloren.")) {
      return;
    }
    try {
      const r = await api.post(`/document-templates/reseed?force=${force ? "true" : "false"}`);
      toast.success(`Reseed: +${r.data.inserted} neu, ${r.data.updated} aktualisiert`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reseed fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] text-zinc-100 flex items-center justify-center">
        <Loader2 className="animate-spin text-[#00E575]" size={28} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] text-zinc-50">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-[#00E575]/15 text-[#00E575] border border-[#00E575]/40 rounded-full px-2.5 py-0.5 text-[11px] font-mono uppercase tracking-wider font-bold mb-2">
              <Layers size={11} /> Iter89m · Phase 1a
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold leading-tight">Dokumenten-Verwaltung</h1>
            <p className="text-zinc-400 mt-1 text-sm">
              Zentrale Registry aller {items.length} System-Vorlagen. Pflegen Sie Kategorien, Pipeline-Stages und Empfänger.
              Der Generator-Kern bleibt unangetastet (hart-codierte PDF-Renderer).
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => reseed(false)}
              data-testid="doctpl-reseed"
              className="h-10 px-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-[13px] font-semibold inline-flex items-center gap-1.5"
            >
              <RefreshCw size={14} /> Neue System-Vorlagen laden
            </button>
            <button
              onClick={() => reseed(true)}
              data-testid="doctpl-reseed-force"
              className="h-10 px-3 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 text-rose-200 text-[13px] font-semibold inline-flex items-center gap-1.5 border border-rose-500/40"
            >
              <RefreshCw size={14} /> Defaults zurücksetzen
            </button>
          </div>
        </div>

        {/* Filter */}
        <div className="flex flex-wrap gap-2 mb-5">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Suche nach Name, Key oder Beschreibung …"
              data-testid="doctpl-search"
              className="w-full h-10 pl-9 pr-3 bg-[#18181B] border border-zinc-800 rounded-lg text-[13px] focus:border-[#00E575] outline-none"
            />
          </div>
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            data-testid="doctpl-cat-filter"
            className="h-10 px-3 bg-[#18181B] border border-zinc-800 rounded-lg text-[13px]"
          >
            <option value="">Alle Kategorien</option>
            {(meta?.categories || []).map((c) => (
              <option key={c.key} value={c.key}>{c.label}</option>
            ))}
          </select>
        </div>

        {/* Groups */}
        {(meta?.categories || []).map((cat) => {
          const list = grouped[cat.key];
          if (!list?.length) return null;
          return (
            <section key={cat.key} className="mb-8" data-testid={`doctpl-cat-${cat.key}`}>
              <h2 className={`font-display font-bold text-sm uppercase tracking-wider mb-3 inline-block px-3 py-1 rounded-lg border ${CATEGORY_COLORS[cat.key] || CATEGORY_COLORS.sonstige}`}>
                {cat.label} <span className="opacity-60">· {list.length}</span>
              </h2>
              <div className="grid gap-3">
                {list.map((tpl) => (
                  <TemplateCard
                    key={tpl.id}
                    tpl={tpl}
                    meta={meta}
                    onSave={(changes) => patch(tpl.id, changes)}
                    saving={savingId === tpl.id}
                  />
                ))}
              </div>
            </section>
          );
        })}

        {items.length === 0 && (
          <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-6 text-center">
            <AlertCircle className="mx-auto mb-2 text-amber-400" size={28} />
            <p className="text-amber-200 font-semibold">Registry ist leer.</p>
            <p className="text-zinc-400 text-sm mt-1">
              Klicken Sie „Neue System-Vorlagen laden", um die 12 System-Templates zu seeden.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}


function TemplateCard({ tpl, meta, onSave, saving }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState(() => ({
    name: tpl.name,
    description: tpl.description || "",
    category: tpl.category,
    stage_keys: tpl.stage_keys || [],
    recipients: tpl.recipients || [],
    is_active: tpl.is_active !== false,
  }));

  useEffect(() => {
    setDraft({
      name: tpl.name,
      description: tpl.description || "",
      category: tpl.category,
      stage_keys: tpl.stage_keys || [],
      recipients: tpl.recipients || [],
      is_active: tpl.is_active !== false,
    });
  }, [tpl]);

  const dirty = useMemo(() => {
    return (
      draft.name !== tpl.name ||
      draft.description !== (tpl.description || "") ||
      draft.category !== tpl.category ||
      JSON.stringify(draft.stage_keys) !== JSON.stringify(tpl.stage_keys || []) ||
      JSON.stringify(draft.recipients) !== JSON.stringify(tpl.recipients || []) ||
      draft.is_active !== (tpl.is_active !== false)
    );
  }, [draft, tpl]);

  const stageLabels = useMemo(() => {
    const map = {};
    (meta?.stages || []).forEach((s) => { map[s.key] = s.label; });
    return map;
  }, [meta]);

  const recipientLabels = useMemo(() => {
    const map = {};
    (meta?.recipients || []).forEach((r) => { map[r.key] = r.label; });
    return map;
  }, [meta]);

  const toggle = (key, arrKey, valid) => {
    setDraft((d) => {
      const set = new Set(d[arrKey] || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...d, [arrKey]: Array.from(set).filter((k) => valid.has(k)) };
    });
  };

  const stageKeys = new Set((meta?.stages || []).map((s) => s.key));
  const recipientKeys = new Set((meta?.recipients || []).map((r) => r.key));

  const genType = (tpl.generator || {}).type;

  return (
    <div
      className={`rounded-xl border transition ${
        tpl.is_active === false
          ? "bg-zinc-900/30 border-zinc-800 opacity-70"
          : "bg-[#18181B] border-zinc-800 hover:border-zinc-700"
      }`}
      data-testid={`doctpl-card-${tpl.key}`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-4 flex items-start gap-3"
      >
        <FileText size={20} className="text-zinc-400 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <div className="font-semibold text-[14px] text-zinc-100">{tpl.name}</div>
            <div className="text-[10.5px] font-mono text-zinc-500 uppercase">{tpl.key}</div>
            <div className="text-[10.5px] font-mono text-emerald-300">v{tpl.version || 1}</div>
            {genType === "upload_only" && (
              <span className="text-[10px] font-mono uppercase bg-cyan-500/20 text-cyan-200 px-1.5 py-0.5 rounded">
                Upload
              </span>
            )}
            {genType === "code" && (
              <span className="text-[10px] font-mono uppercase bg-zinc-800 text-zinc-400 px-1.5 py-0.5 rounded" title="Hart-codierter Python-Renderer (nicht editierbar)">
                Code
              </span>
            )}
          </div>
          <p className="text-[12.5px] text-zinc-400 mt-1 leading-snug line-clamp-2">
            {tpl.description}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {(tpl.stage_keys || []).map((s) => (
              <span key={s} className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-200 border border-emerald-500/30">
                {stageLabels[s] || s}
              </span>
            ))}
            {(tpl.recipients || []).map((r) => (
              <span key={r} className="text-[11px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-200 border border-sky-500/30">
                <Users size={9} className="inline -mt-0.5 mr-0.5" /> {recipientLabels[r] || r}
              </span>
            ))}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-zinc-800 p-4 space-y-4 bg-[#0F0F12]/50">
          {/* Anzeigename */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">Anzeigename</label>
            <input
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              data-testid={`doctpl-name-${tpl.key}`}
              className="w-full h-9 px-2.5 bg-[#09090B] border border-zinc-800 rounded-md text-[13px]"
            />
          </div>

          {/* Beschreibung */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">Beschreibung</label>
            <textarea
              rows={2}
              value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })}
              data-testid={`doctpl-desc-${tpl.key}`}
              className="w-full px-2.5 py-1.5 bg-[#09090B] border border-zinc-800 rounded-md text-[13px]"
            />
          </div>

          {/* Kategorie */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1">Kategorie</label>
            <select
              value={draft.category}
              onChange={(e) => setDraft({ ...draft, category: e.target.value })}
              data-testid={`doctpl-cat-${tpl.key}`}
              className="h-9 px-2.5 bg-[#09090B] border border-zinc-800 rounded-md text-[13px]"
            >
              {(meta?.categories || []).map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>

          {/* Stages */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1.5">Pipeline-Stages · wo das Dokument angeboten wird</label>
            <div className="flex flex-wrap gap-1.5">
              {(meta?.stages || []).map((s) => {
                const active = draft.stage_keys.includes(s.key);
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => toggle(s.key, "stage_keys", stageKeys)}
                    data-testid={`doctpl-stage-${tpl.key}-${s.key}`}
                    className={`text-[11.5px] px-2 py-1 rounded-md border transition ${
                      active
                        ? "bg-emerald-500/15 text-emerald-200 border-emerald-400"
                        : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="text-[11px] uppercase tracking-wider text-zinc-400 font-semibold block mb-1.5">Empfänger</label>
            <div className="flex flex-wrap gap-1.5">
              {(meta?.recipients || []).map((r) => {
                const active = draft.recipients.includes(r.key);
                return (
                  <button
                    key={r.key}
                    type="button"
                    onClick={() => toggle(r.key, "recipients", recipientKeys)}
                    data-testid={`doctpl-rec-${tpl.key}-${r.key}`}
                    className={`text-[11.5px] px-2 py-1 rounded-md border transition ${
                      active
                        ? "bg-sky-500/15 text-sky-200 border-sky-400"
                        : "bg-zinc-900 text-zinc-400 border-zinc-800 hover:border-zinc-600"
                    }`}
                  >
                    <Users size={10} className="inline -mt-0.5 mr-0.5" /> {r.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Aktiv-Toggle + Save */}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer">
              <input
                type="checkbox"
                checked={draft.is_active}
                onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
                data-testid={`doctpl-active-${tpl.key}`}
                className="accent-[#00E575]"
              />
              <span>Aktiv im Cockpit anzeigen</span>
            </label>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setDraft({
                  name: tpl.name,
                  description: tpl.description || "",
                  category: tpl.category,
                  stage_keys: tpl.stage_keys || [],
                  recipients: tpl.recipients || [],
                  is_active: tpl.is_active !== false,
                })}
                disabled={!dirty || saving}
                className="h-9 px-3 rounded-md bg-zinc-800 hover:bg-zinc-700 text-[12.5px] disabled:opacity-40"
              >
                Verwerfen
              </button>
              <button
                type="button"
                onClick={() => onSave(draft)}
                disabled={!dirty || saving}
                data-testid={`doctpl-save-${tpl.key}`}
                className="h-9 px-3 rounded-md bg-[#00E575] text-[#09090B] font-bold text-[12.5px] hover:bg-[#00C964] disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Speichern
              </button>
            </div>
          </div>

          {tpl.system_defaults && (
            <div className="text-[11px] text-zinc-500 bg-zinc-900/50 rounded-md p-2 flex items-start gap-1.5">
              <Info size={11} className="mt-0.5 flex-shrink-0" />
              <div>
                <b>System-Default:</b> {tpl.system_defaults.category} · {(tpl.system_defaults.stage_keys || []).join(", ")} · {(tpl.system_defaults.recipients || []).join(", ")}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
